import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { signOut } from '../api';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { ConfirmWithReason } from './confirm-with-reason';
import { NavIcon } from './nav-icon';
import { endSignOut, runSignOut, SignOutLabel, useSigningOut } from './sign-out-progress';

// Hold-to-confirm sign-out (design rail footer, `soArmed/soHold/soExit`). Two
// ways out, both deliberate: press and HOLD for HOLD_MS (a fill sweeps the pill;
// releasing early cancels), or click/Enter for a "Sign out?" confirm dialog — the
// keyboard path. Signing out revokes the refresh cookie server-side and drops the
// in-memory token (AP-D4); the session reload then shows the sign-in gate.
// sign-out-progress.tsx runs it once across both copies (rail + account menu):
// every sign-out control disables, shows "Signing out…", and the veil covers the
// console until the gate swaps in — after a failed call too.

export const HOLD_MS = 900;

export function SignOutButton({ className = 'rail__signout' }: { className?: string }) {
  const { t } = useLanguage();
  const { reload } = useSession();
  const signingOut = useSigningOut();
  const [holding, setHolding] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fired = useRef(false);

  const leave = () =>
    runSignOut(signOut, () => {
      // The console stays mounted (the gate swaps in the sign-in page), so the flag
      // is cleared here rather than by a page unload.
      reload();
      endSignOut();
    });

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setHolding(false);
  };
  useEffect(() => clear, []);

  const start = (e: ReactPointerEvent) => {
    if (e.button !== 0 || signingOut) return;
    fired.current = false;
    setHolding(true);
    timer.current = setTimeout(() => {
      fired.current = true;
      setHolding(false);
      void leave();
    }, HOLD_MS);
  };

  return (
    <>
      <button
        type="button"
        className={holding ? `${className} is-holding` : className}
        disabled={signingOut}
        aria-busy={signingOut}
        style={{ ['--hold-ms' as string]: `${HOLD_MS}ms` }}
        title={t('admin.shell.signout_hold')}
        onPointerDown={start}
        onPointerUp={clear}
        onPointerLeave={clear}
        onPointerCancel={clear}
        onClick={() => {
          // A completed hold already signed out; a short press asks instead.
          if (fired.current || signingOut) return;
          clear();
          setConfirm(true);
        }}
      >
        <span className="rail__signout-fill" aria-hidden="true" />
        <SignOutLabel label={t('admin.signout')} icon={<NavIcon name="logout" size={16} />} />
      </button>
      <ConfirmWithReason
        open={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={leave}
        title={t('admin.shell.signout_title')}
        body={t('admin.shell.signout_body')}
        confirmLabel={t('admin.signout')}
        cancelLabel={t('admin.shell.signout_stay')}
        tone="danger"
      />
    </>
  );
}
